import anime from "animejs";
import { Settings } from "./settings";
import { FEATURES_CONFIG, WIN_TYPES } from "./featuresConfig";

export const animateTextCounter = (
  targetRef,
  startValue,
  endValue,
  duration,
  onUpdate,
  onComplete
) => {
  if (!targetRef.current) return () => {};

  const obj = { value: startValue };

  const animation = anime({
    targets: obj,
    value: endValue,
    duration: duration,
    easing: "linear",
    update: () => {
      if (targetRef.current && onUpdate) {
        onUpdate(Math.floor(obj.value));
      }
    },
    complete: () => {
      if (targetRef.current && onComplete) {
        onComplete();
      }
    },
  });

  // Return cancel function
  return () => {
    animation.pause();
    if (onComplete) onComplete();
  };
};

/**
 * Format currency value with dynamic configuration
 *
 * @example
 * // Format with default config (100 subunits, 2 decimals)
 * formatCurrency(1000, { subunits: 100, exponent: 2 }, "en-US") // "10.00"
 */
export const formatCurrency = (value, config = {}, locale = "en-US") => {
  const { subunits = 100, exponent = 2 } = config;

  // Convert from subunits to main currency units
  const mainValue = value / subunits;

  // Note: toLocaleString() natively handles invalid locales by falling back to the default locale
  // No explicit validation needed - the browser/Node.js handles this gracefully
  return mainValue.toLocaleString(locale, {
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  });
};

/**
 * Format currency value with dynamic configuration from Redux store
 *
 * @example
 * // Simple usage - no config needed
 * const formatted = formatToCustomLocale(1000); // "10.00" (or "10,00" for de-DE locale)
 */
export const formatToCustomLocale = (value) => {
  // Import store dynamically to avoid circular dependencies
  // Store is available globally after app initialization
  let currencyConfig = { subunits: 100, exponent: 2 }; // Defaults
  let locale = "en-US"; // Default locale

  try {
    // Try to get currency config from Redux store
    // This works because the store is a singleton and available globally
    const store = require("./State/Store/store").default;
    const state = store.getState();

    // Get currency config from state if available
    if (state?.game?.currencyConfig) {
      currencyConfig = state.game.currencyConfig;
    }

    // Get locale from window options if available
    if (window.__OPTIONS__?.locale) {
      locale = window.__OPTIONS__.locale;
    }
  } catch (error) {
    // If store access fails (e.g., during tests or before initialization),
    // use defaults silently
  }

  // Use the formatCurrency function with retrieved config
  return formatCurrency(value, currencyConfig, locale);
};

/**
 * Simple parser for autoplay input fields
 * Removes commas and converts to subunits
 *
 * @param {string} input - User input like "1,500.50" or "1500.5"
 * @param {number} subunits - Currency subunits (default 100)
 * @param {number} exponent - Max decimal places (default 2)
 * @returns {string} - Raw value in subunits as string, or empty string if invalid
 *
 * @example
 * parseAutoplayInput("1,500.50", 100, 2) // Returns "150050"
 * parseAutoplayInput("1,000", 100, 2) // Returns "100000"
 * parseAutoplayInput("", 100, 2) // Returns ""
 */
export const parseAutoplayInput = (input, subunits = 100, exponent = 2) => {
  if (!input || input.trim() === "") {
    return "";
  }

  // Remove all commas (thousands separators)
  let cleaned = input.replace(/,/g, "");

  // Parse as float
  let value = parseFloat(cleaned);
  if (isNaN(value)) {
    return "";
  }

  // Convert to subunits
  let subunitValue = Math.round(value * subunits);

  return subunitValue.toString();
};

/**
 * Parse autoplay input with dynamic configuration from Redux store
 *
 * @param {string} input - User input like "1,500.50"
 * @returns {string} - Raw value in subunits as string
 *
 * @example
 * parseAutoplayInputFromStore("1,500.50") // Returns "150050"
 */
export const parseAutoplayInputFromStore = (input) => {
  if (!input || input.trim() === "") {
    return "";
  }

  // Get currency config from Redux
  let subunits = 100;
  let exponent = 2;

  try {
    const store = require("./State/Store/store").default;
    const state = store.getState();

    if (state?.game?.currencyConfig) {
      subunits = state.game.currencyConfig.subunits || 100;
      exponent = state.game.currencyConfig.exponent || 2;
    }
  } catch (error) {
    // Use defaults
  }

  return parseAutoplayInput(input, subunits, exponent);
};

/**
 * Validate and format autoplay input with decimal support
 * Allows dot character and limits decimal places to exponent
 *
 * @param {string} rawValue - Raw input from user (e.g., "1111.2")
 * @param {number} exponent - Max decimal places (default 2)
 * @returns {string} - Validated and formatted value (e.g., "1111.21")
 *
 * @example
 * validateAutoplayInput("1111.219", 2) // Returns "1111.21"
 * validateAutoplayInput("1111..21", 2) // Returns "1111.21"
 * validateAutoplayInput(".50", 2) // Returns ".50"
 * validateAutoplayInput("00.50", 2) // Returns "0.50"
 */
export const validateAutoplayInput = (rawValue, exponent = 2) => {
  // Remove any non-numeric characters except dot
  let cleaned = rawValue.replace(/[^0-9.]/g, '');

  // Only allow one dot
  const dotCount = (cleaned.match(/\./g) || []).length;
  if (dotCount > 1) {
    const firstDotIndex = cleaned.indexOf('.');
    cleaned = cleaned.substring(0, firstDotIndex + 1) +
              cleaned.substring(firstDotIndex + 1).replace(/\./g, '');
  }

  // Limit integer part to 12 digits and decimal places to exponent
  if (cleaned.includes('.')) {
    const [integerPart, decimalPart] = cleaned.split('.');
    // Limit integer part to 12 digits
    const limitedInteger = integerPart.substring(0, 12);
    const limitedDecimal = decimalPart.substring(0, exponent);
    cleaned = limitedInteger + '.' + limitedDecimal;
  } else {
    // No decimal point - limit entire value to 12 digits
    cleaned = cleaned.substring(0, 12);
  }

  // Prevent leading zeros (except "0" or "0.xx")
  if (cleaned.length > 1 && cleaned[0] === '0' && cleaned[1] !== '.') {
    cleaned = cleaned.replace(/^0+/, '');
  }

  return cleaned;
};

/**
 * Validate autoplay input with dynamic configuration from Redux store
 *
 * @param {string} rawValue - Raw input from user
 * @returns {string} - Validated and formatted value
 *
 * @example
 * validateAutoplayInputFromStore("1111.219") // Returns "1111.21" (with exponent from Redux)
 */
export const validateAutoplayInputFromStore = (rawValue) => {
  // Get currency config from Redux
  let exponent = 2;

  try {
    const store = require("./State/Store/store").default;
    const state = store.getState();

    if (state?.game?.currencyConfig) {
      exponent = state.game.currencyConfig.exponent || 2;
    }
  } catch (error) {
    // Use defaults
  }

  return validateAutoplayInput(rawValue, exponent);
};

// Enhanced responsive font sizing with multiple breakpoints
export const calculateResponsiveFontSize = (width, sizes) => {
  // Default sizes if not provided
  const defaultSizes = {
    mobile: 24, // < 667px
    tablet: 32, // 667px - 1024px
    desktop: 48, // 1024px - 1280px
    large: 56, // ≥ 1280px
  };

  const fontSizes = { ...defaultSizes, ...sizes };

  if (width < 667) {
    return fontSizes.mobile;
  } else if (width < 1024) {
    return fontSizes.tablet;
  } else if (width < 1280) {
    return fontSizes.desktop;
  } else {
    return fontSizes.large;
  }
};

// Fullscreen utilities
export const isIOS = () => {
  const platform = navigator.platform;
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  const userAgent = navigator.userAgent;

  // Check for explicit iOS device in user agent
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return true;
  }

  // iPadOS 13+ reports as MacIntel with touch support
  // Check for MacIntel platform with touch points (iPad in desktop mode)
  if (platform === "MacIntel" && maxTouchPoints > 0) {
    return true;
  }

  // Additional check: Safari on Mac with touch support is likely iPad
  if (/Macintosh/.test(userAgent) && maxTouchPoints > 0) {
    return true;
  }

  return false;
};

/**
 * Detect if browser is Safari
 */
export const isSafari = () => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

// ============================================================================
// iOS Safari Fullscreen - Event-driven Handler
// ============================================================================

// State tracking
let iosSafariAppliedState = '';
let iosSafariScrollElement = null;
let iosSafariScreenHeight = 0;
let iosSafariOnStateChange = null;

// Threshold: if viewport < 90% of screen height, bars are visible
const FULLSCREEN_HEIGHT_THRESHOLD = 0.9;

/**
 * Check if iOS Safari bars are currently visible (need scroll to hide)
 * @returns {boolean} True if bars are visible and need hiding
 */
export const isIosSafariBarsVisible = () => {
  if (!isIOS()) return false;

  const viewportHeight = window.visualViewport?.height || window.innerHeight;
  iosSafariScreenHeight = Math.min(window.screen.width, window.screen.height);

  return viewportHeight < iosSafariScreenHeight * FULLSCREEN_HEIGHT_THRESHOLD;
};

/**
 * Create the scroll spacer element
 */
const createScrollElement = () => {
  if (iosSafariScrollElement) return;

  iosSafariScrollElement = document.createElement('div');
  iosSafariScrollElement.id = '__ios_spacer__';
  iosSafariScreenHeight = Math.min(window.screen.width, window.screen.height);
  iosSafariScrollElement.style.height = (iosSafariScreenHeight + 3000) + 'px';
  iosSafariScrollElement.style.width = '100%';
  iosSafariScrollElement.style.position = 'absolute';
  document.body.appendChild(iosSafariScrollElement);
};

/**
 * Apply "armed" state - make page scrollable to hide Safari bars
 */
const applyArmedState = () => {
  if (!iosSafariScrollElement) {
    createScrollElement();
  }

  const tallHeight = iosSafariScreenHeight + 3000;

  document.documentElement.style.position = 'static';
  document.documentElement.style.height = '100%';
  document.documentElement.style.overflow = 'unset';

  document.body.style.position = 'static';
  document.body.style.overflow = 'unset';
  document.body.style.height = tallHeight + 'px';
  document.body.style.touchAction = 'pan-y';

  const canvas = document.querySelector('canvas');
  if (canvas) {
    canvas.style.position = 'fixed';
  }

  iosSafariScrollElement.style.zIndex = '1000';
  iosSafariScrollElement.style.height = tallHeight + 'px';
};

/**
 * Apply "locked" state - page not scrollable, bars hidden
 */
const applyLockedState = () => {
  document.documentElement.style.height = '100%';
  document.documentElement.style.overflow = 'hidden';

  document.body.style.overflow = 'hidden';
  document.body.style.height = '100%';
  document.body.style.touchAction = '';

  if (iosSafariScrollElement) {
    iosSafariScrollElement.style.zIndex = '-1';
  }

  window.dispatchEvent(new Event('resize'));
};

/**
 * Start iOS Safari fullscreen handler
 * Monitors viewport changes and automatically manages scroll state based on bar visibility
 * @param {Function} onStateChange - Called with (needsScroll: boolean) when state changes
 * @returns {Function} Cleanup function to remove event listeners
 */
export const startIosSafariFullscreenHandler = (onStateChange) => {
  if (!isIOS()) {
    return () => {};
  }

  iosSafariOnStateChange = onStateChange;

  const checkBarState = () => {
    const isLandscape = window.innerWidth > window.innerHeight;
    const needsScroll = isLandscape && isIosSafariBarsVisible();
    const currentState = `${needsScroll}`;

    if (iosSafariAppliedState !== currentState) {
      iosSafariAppliedState = currentState;

      if (needsScroll) {
        applyArmedState();
      } else {
        applyLockedState();
      }

      if (iosSafariOnStateChange) {
        iosSafariOnStateChange(needsScroll);
      }
    }
  };

  // Listen to viewport and orientation changes
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', checkBarState);
  }
  window.addEventListener('scroll', checkBarState, { passive: true });
  window.addEventListener('orientationchange', checkBarState);

  checkBarState();

  return () => {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', checkBarState);
    }
    window.removeEventListener('scroll', checkBarState);
    window.removeEventListener('orientationchange', checkBarState);
    iosSafariAppliedState = '';
    iosSafariOnStateChange = null;
  };
};

/**
 * Stop iOS Safari fullscreen handler
 * @deprecated Use the cleanup function returned by startIosSafariFullscreenHandler instead
 */
export const stopIosSafariFullscreenHandler = () => {
  iosSafariAppliedState = '';
  iosSafariOnStateChange = null;
};

/**
 * Check if iOS Safari fullscreen overlay should show
 * Returns true if on iOS Safari landscape and bars are visible
 */
export const shouldShowIosSafariOverlay = () => {
  if (!isIOS()) return false;

  const isLandscape = window.innerWidth > window.innerHeight;
  return isLandscape && isIosSafariBarsVisible();
};

// ============================================================================
// Legacy functions (kept for compatibility, but simplified)
// ============================================================================

/**
 * @deprecated Use startIosSafariFullscreenHandler instead
 */
export const armScroll = () => {
  applyArmedState();
};

/**
 * @deprecated Use startIosSafariFullscreenHandler instead
 */
export const lockScroll = () => {
  applyLockedState();
};

/**
 * @deprecated Not needed with event-driven approach
 */
export const waitForStableViewport = (callback) => {
  return () => {};
};

/**
 * Detect if Safari toolbar is already hidden
 * @returns {Object} { alreadyHidden: boolean, currentHeight: number }
 */
export const detectAlreadyHidden = () => {
  if (!isIOS()) {
    return { alreadyHidden: false, currentHeight: 0 };
  }

  const vvHeight = window.visualViewport?.height || window.innerHeight;
  const screenHeight = window.screen.height;
  const scrollY = window.scrollY || window.pageYOffset;
  const likelyHidden = (vvHeight > screenHeight * 0.9) && (scrollY > 0);

  return {
    alreadyHidden: likelyHidden,
    currentHeight: vvHeight
  };
};

/**
 * Pause iOS fullscreen state for portrait mode
 * Demotes spacer but preserves scroll position for smooth orientation changes
 */
export const pauseIosFullscreenState = () => {
  if (iosSafariScrollElement) {
    iosSafariScrollElement.style.zIndex = '-1';
  }
};

/**
 * Complete reset of iOS fullscreen state
 */
export const resetIosFullscreenState = () => {
  stopIosSafariFullscreenHandler();

  document.documentElement.style.overflow = '';
  document.documentElement.style.position = '';
  document.documentElement.style.height = '';
  document.documentElement.style.touchAction = '';

  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.height = '';
  document.body.style.touchAction = '';

  const canvas = document.querySelector('canvas');
  if (canvas) {
    canvas.style.position = '';
    canvas.style.pointerEvents = '';
    canvas.style.touchAction = '';
  }

  if (iosSafariScrollElement && iosSafariScrollElement.parentNode) {
    iosSafariScrollElement.parentNode.removeChild(iosSafariScrollElement);
    iosSafariScrollElement = null;
  }

  window.scrollTo(0, 0);

  // Clear session storage flag so overlay can show again if needed
  sessionStorage.removeItem('iosFullscreenDismissed');
};


/**
 * Check if iOS fullscreen overlay should show
 * Only shows on Safari browser on iOS (not Chrome, Firefox, Edge, etc.)
 */
export const shouldShowIosFullscreenOverlay = () => {
  const isIOSDevice = isIOS();
  const dismissed = sessionStorage.getItem('iosFullscreenDismissed');
  const userAgent = navigator.userAgent;
  const isSafariBrowser = /Safari/.test(userAgent) &&
                          !/CriOS/.test(userAgent) &&  // Chrome iOS
                          !/FxiOS/.test(userAgent) &&  // Firefox iOS
                          !/EdgiOS/.test(userAgent);   // Edge iOS

  if (!isIOSDevice) return false;
  if (dismissed) return false;
  return isSafariBrowser;
};

export const isFullscreenSupported = () => {
  return (
    !isIOS() &&
    (document.fullscreenEnabled ||
      document.webkitFullscreenEnabled ||
      document.mozFullScreenEnabled ||
      document.msFullscreenEnabled)
  );
};

export const isInFullscreen = () => {
  return !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  );
};

/**

 * Detects if user is on an actual physical desktop computer
 * Uses screen size and touch capability to distinguish from mobile/tablet
 * This is different from layout device detection which uses window size
 * @returns {boolean} True if physical desktop computer
 */
export const isPhysicalDesktop = () => {
  // Check 1: Actual screen width (monitor size, not window size)
  // Desktop monitors are typically >= 1366px
  const hasDesktopScreen = window.screen.width >= 1366;

  // Check 2: No touch support (desktop computers don't have touch)
  // maxTouchPoints = 0 means no touch capability
  const hasNoTouch = navigator.maxTouchPoints === 0;

  // Desktop ONLY if: no touch AND large screen
  // Note: Don't use userAgentData because DevTools doesn't simulate it correctly
  return hasNoTouch && hasDesktopScreen;
};

/**
 * Attempts to enter fullscreen mode if conditions are met
 * Checks actual browser fullscreen state and only enters if not already in fullscreen
 * Only triggers on actual mobile/tablet devices (not resized desktop browsers)
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} device - Device type (from PixiSettings.DEVICE) - not used anymore
 */
export const attemptFullscreenToggle = (dispatch, device) => {
  // Lazy import to avoid circular dependency with settingsSlice
  const store = require("./State/Store/store").default;
  const { toggleFullscreen } = require("./State/actions");

  const state = store.getState();
  const { fullscreenSupported } = state.settings;

  // Use physical device detection instead of layout device
  const isActualDesktop = isPhysicalDesktop();

  // Check actual browser state - only enter if not already in fullscreen
  // This handles cases where user manually exits fullscreen (e.g., F11)
  const actuallyInFullscreen = isInFullscreen();

  // Only trigger fullscreen on actual mobile/tablet devices
  if (!actuallyInFullscreen && !isActualDesktop && !isIOS() && fullscreenSupported) {
    dispatch(toggleFullscreen());
  }
};

// Add fullscreen change event listeners
export const addFullscreenChangeListener = (callback) => {
  const events = [
    "fullscreenchange",
    "webkitfullscreenchange",
    "mozfullscreenchange",
    "MSFullscreenChange",
  ];

  events.forEach((event) => {
    document.addEventListener(event, callback);
  });

  // Return cleanup function
  return () => {
    events.forEach((event) => {
      document.removeEventListener(event, callback);
    });
  };
};

/**
 * Get win type based on win amount and bet size using provider-specific thresholds
 * Works dynamically with any RGS provider configuration
 * @param {number} winAmount - Win amount in currency units
 * @param {number} betSize - Bet size in currency units
 * @param {string} roundType - Round type (e.g., "feature", "regular")
 * @returns {string} - Win type string that can be used for both display and sound
 */
export const getWinType = (winAmount, betSize = null, roundType = null) => {
  // Return empty string for invalid inputs
  if (!winAmount || winAmount <= 0 || !betSize || betSize <= 0) {
    return "";
  }

  const multiplier = winAmount / betSize;

  // Get all thresholds and sort them from highest to lowest
  const thresholdEntries = Object.entries(FEATURES_CONFIG.WIN_THRESHOLDS).sort(
    ([, a], [, b]) => b - a
  );

  // Check thresholds from highest to lowest
  for (const [thresholdKey, thresholdValue] of thresholdEntries) {
    if (multiplier >= thresholdValue) {
      // Convert threshold key to win type key (e.g., "BIG_WIN_THRESHOLD" -> "BIG_WIN")
      const winTypeKey = thresholdKey.replace("_THRESHOLD", "");
      const result = WIN_TYPES[winTypeKey] || "";
      
      return result;
    }
  }

  return ""; // Not a win or below minimum threshold
};

// Removed unused calculateWinAnimationThresholds function - replaced with simpler logic in WinCounter

/**
 * Get native dimensions of a symbol from spritesheet JSON data
 * @param {string} symbolName - Name of the symbol (e.g., "reel_symbol_1")
 * @param {Object} spriteSheetData - The parsed JSON data from the spritesheet
 * @returns {Object} - { width, height } of the symbol's native dimensions
 */
export const getSymbolNativeDimensions = (symbolName, spriteSheetData) => {
  const frameData = spriteSheetData?.frames?.[`${symbolName}.png`];
  if (!frameData) {
    // Fallback to common size if not found
    return { width: Settings.SYMBOL_WIDTH, height: Settings.SYMBOL_HEIGHT };
  }

  const { w, h } = frameData.sourceSize;
  return { width: w, height: h };
};

/**
 * Finds the highest bet amount that the user can afford with their current balance
 */
export const findHighestAffordableBet = (balance, bets) => {
  if (!bets || bets.length === 0 || balance < 0) {
    return null;
  }

  // Find the highest bet that is <= balance
  for (let i = bets.length - 1; i >= 0; i--) {
    if (bets[i] <= balance) {
      return bets[i];
    }
  }

  return null;
};

/**
 * Determines what action to take when user doesn't have enough balance for current bet
 */
export const checkBalanceAndBetStatus = (balance, currentBet, bets) => {
  // Check if user can afford ANY bet from the bets array
  const affordableBet = findHighestAffordableBet(balance, bets);

  // If no bet is affordable at all - trigger fake spin
  if (affordableBet === null) {
    return { action: "fake_spin" };
  }

  // Has credits and current bet is affordable
  if (balance >= currentBet) {
    return { action: "sufficient" };
  }

  // Has credits but not enough for current bet, but can afford a smaller bet
  return { action: "insufficient_credits", affordableBet };
};

/**
 * Find hold and win outro animations from available spine animations
 * @param {string[]} availableAnimations - Array of available animation names from spine
 * @returns {Object} Object with inAnimation, idleAnimation, outAnimation properties
 */
export const getHoldAndWinOutroAnimations = (availableAnimations) => {
  return {
    inAnimation: availableAnimations.find((anim) => anim === "in") || null,
    idleAnimation:
      availableAnimations.find((anim) => anim === "idle") || null,
    outAnimation: availableAnimations.find((anim) => anim === "out") || null,
  };
};

// Animation state management to prevent concurrent animations
export const activeAnimations = new Map();

/**
 * Helper to get animation key with type for concurrent animation support
 * Allows multiple animation types to run on the same sprite simultaneously
 * @param {PIXI.Sprite} sprite - The sprite to generate key for
 * @param {string} type - Animation type ('press' or 'rotation')
 * @returns {string} Unique key for this sprite + animation type combination
 */
const getAnimationKey = (sprite, type) => {
  if (!sprite._animationId) {
    sprite._animationId = Math.random().toString(36).substr(2, 9);
  }
  return `${sprite._animationId}_${type}`;
};

/**
 * Creates a reusable button press animation for PIXI sprites
 * @param {PIXI.Sprite} sprite - The sprite to animate
 * @param {Object} options - Animation options
 * @param {number} options.targetWidth - Explicit target width (prevents dimension drift on rapid clicks)
 * @param {number} options.targetHeight - Explicit target height (prevents dimension drift on rapid clicks)
 * @param {Function} options.onComplete - Callback fired when animation fully completes
 * @returns {anime} The anime.js animation instance
 */
export const createButtonPressAnimation = (sprite, options = {}) => {
  if (!sprite) return null;

  const animKey = getAnimationKey(sprite, "press");
  const bounceKey = animKey + "_bounce";

  // Stop any existing press or bounce-back animation
  const existingPress = activeAnimations.get(animKey);
  if (existingPress) {
    existingPress.pause();
    activeAnimations.delete(animKey);
  }
  const existingBounce = activeAnimations.get(bounceKey);
  if (existingBounce) {
    existingBounce.pause();
    activeAnimations.delete(bounceKey);
  }

  // Use explicit dimensions if provided (preferred), otherwise store current dimensions
  let originalWidth, originalHeight;

  if (options.targetWidth !== undefined && options.targetHeight !== undefined) {
    // Explicit dimensions provided - use these as the source of truth
    originalWidth = options.targetWidth;
    originalHeight = options.targetHeight;

    // Store them for fallback
    sprite._originalWidth = originalWidth;
    sprite._originalHeight = originalHeight;
  } else {
    // Fallback: Store the original width and height (before any animations)
    if (sprite._originalWidth === undefined) {
      sprite._originalWidth = sprite.width;
      sprite._originalHeight = sprite.height;
      sprite._originalEventMode = sprite.eventMode;
    }

    originalWidth = sprite._originalWidth;
    originalHeight = sprite._originalHeight;
  }

  // Check if sprite still exists before starting animation
  if (!sprite || sprite.destroyed) {
    activeAnimations.delete(animKey);
    return null;
  }

  // DO NOT reset dimensions here — animate from wherever the sprite currently is
  // This prevents the snap/jitter when interrupting a bounce-back

  // Create press animation (shrink)
  const animation = anime({
    targets: sprite,
    width: originalWidth * 0.97,
    height: originalHeight * 0.97,
    duration: 30,
    easing: "easeOutQuad",
    update: function() {
      if (!sprite || sprite.destroyed) {
        animation.pause();
        activeAnimations.delete(animKey);
      }
    },
    complete: function () {
      activeAnimations.delete(animKey);

      if (!sprite || sprite.destroyed) {
        return;
      }

      // Animate back to original dimensions (bounce-back)
      const bounceAnimation = anime({
        targets: sprite,
        width: originalWidth,
        height: originalHeight,
        duration: 30,
        easing: "easeOutQuad",
        update: function() {
          if (!sprite || sprite.destroyed) {
            activeAnimations.delete(bounceKey);
          }
        },
        complete: function () {
          activeAnimations.delete(bounceKey);

          if (!sprite || sprite.destroyed) {
            return;
          }

          // Ensure final dimensions are exactly correct
          sprite.width = originalWidth;
          sprite.height = originalHeight;

          // Clean up stored original values after animation completes
          const rotationKey = getAnimationKey(sprite, "rotation");
          if (!activeAnimations.has(rotationKey)) {
            delete sprite._originalWidth;
            delete sprite._originalHeight;
            delete sprite._originalEventMode;
          }

          // Fire completion callback if provided
          if (options.onComplete) {
            options.onComplete();
          }
        },
      });

      // Track bounce-back so it can be interrupted by next press
      activeAnimations.set(bounceKey, bounceAnimation);
    },
  });

  // Mark animation start time for conflict detection
  animation._startTime = Date.now();

  // Track this animation with type-specific key
  activeAnimations.set(animKey, animation);

  return animation;
};

export const safeInit = (callback) => {
  requestAnimationFrame(() => {
    callback();
  });
};

/**
 * Scale up animation for Pixi containers
 * @param {PIXI.Container} target - The container to animate
 * @param {number} scaleTarget - Target scale value (default 1.4)
 * @param {number} duration - Animation duration in ms (default 150)
 * @returns {anime} The anime.js animation instance
 */
export const createScaleUpAnimation = (
  target,
  scaleTarget = 1.25,
  duration = 150
) => {
  if (!target) return null;

  return anime({
    targets: target.scale,
    x: scaleTarget,
    y: scaleTarget,
    duration: duration,
    easing: "easeInOutQuad",
  });
};

/**
 * Scale down animation for Pixi containers
 * @param {PIXI.Container} target - The container to animate
 * @param {number} duration - Animation duration in ms (default 175)
 * @param {function} onComplete - Optional callback to fire when animation completes
 * @returns {anime} The anime.js animation instance
 */
export const createScaleDownAnimation = (
  target,
  duration = 150,
  onComplete = null
) => {
  if (!target) return null;

  return anime({
    targets: target.scale,
    x: 1,
    y: 1,
    duration: duration,
    easing: "easeInOutQuad",
    complete: onComplete,
  });
};

/**
 * Creates a rotation animation for the play button (separate from scaling)
 * @param {PIXI.Sprite} sprite - The play button sprite to animate
 * @param {Object} options - Animation options
 * @returns {anime} The anime.js animation instance
 */
export const createPlayButtonRotationAnimation = (sprite, options = {}) => {
  if (!sprite) return null;

  const animKey = getAnimationKey(sprite, "rotation");

  // Check if there's an existing ROTATION animation currently active
  const existingAnimation = activeAnimations.get(animKey);
  if (existingAnimation) {
    existingAnimation.pause();
    // Immediately reset to original state to prevent conflicts
    if (sprite._originalRotation !== undefined) {
      // Check if sprite still exists and is not destroyed
      if (sprite && !sprite.destroyed) {
        sprite.rotation = 0; // Reset to 0 degrees
      }
    }
  }

  // Store the original state (before any animations)
  if (sprite._originalRotation === undefined) {
    sprite._originalRotation = sprite.rotation;
    // sprite._originalEventMode = sprite.eventMode; // Not needed - we don't disable eventMode
  }

  // const originalEventMode = sprite._originalEventMode; // Not needed - we don't disable eventMode

  // Check if sprite still exists before starting animation
  if (!sprite || sprite.destroyed) {
    activeAnimations.delete(animKey);
    return null;
  }

  // Force reset to original state at the start to prevent drift
  sprite.rotation = 0; // Start from 0 degrees

  // Don't disable pointer events - allow clicking to stop during rotation animation
  // sprite.eventMode = "none";

  // Create the rotation animation sequence (no scaling)
  const animation = anime({
    targets: sprite,
    // Phase 1: Rotate to the right
    rotation: Math.PI / 2, // 90 degrees to the right
    duration: 142, // Faster
    easing: "easeOutQuad",
    update: function() {
      // Stop animation if sprite is destroyed
      if (!sprite || sprite.destroyed) {
        animation.pause();
        activeAnimations.delete(animKey);
      }
    },
    complete: function () {
      // Check if sprite still exists before phase 2
      if (!sprite || sprite.destroyed) {
        activeAnimations.delete(animKey);
        return;
      }

      // Phase 2: Rotate slightly backwards from initial position
      anime({
        targets: sprite,
        rotation: (-45 * Math.PI) / 180, // -45 degrees (in radians)
        duration: 142, // Faster
        easing: "easeOutQuad",
        update: function() {
          // Stop animation if sprite is destroyed
          if (!sprite || sprite.destroyed) {
            activeAnimations.delete(animKey);
          }
        },
        complete: function () {
          // Check if sprite still exists before phase 3
          if (!sprite || sprite.destroyed) {
            activeAnimations.delete(animKey);
            return;
          }

          // Phase 3: Return to start position
          anime({
            targets: sprite,
            rotation: 0, // Back to start position
            duration: 142, // Faster
            easing: "easeOutQuad",
            update: function() {
              // Stop animation if sprite is destroyed
              if (!sprite || sprite.destroyed) {
                activeAnimations.delete(animKey);
              }
            },
            complete: function () {
              // Check if sprite still exists before cleanup
              if (!sprite || sprite.destroyed) {
                activeAnimations.delete(animKey);
                return;
              }

              // Ensure final state is exactly correct
              sprite.rotation = 0; // Reset to 0 degrees
              // sprite.eventMode = originalEventMode; // Not needed since we don't disable it

              // Clean up animation state
              activeAnimations.delete(animKey);

              // Clean up stored original values after animation completes
              delete sprite._originalRotation;
              delete sprite._originalEventMode;

              // Only clean up dimensions if press animation is not using them
              const pressKey = getAnimationKey(sprite, "press");
              if (!activeAnimations.has(pressKey)) {
                delete sprite._originalWidth;
                delete sprite._originalHeight;
              }
            },
          });
        },
      });
    },
  });

  // Mark animation start time for conflict detection
  animation._startTime = Date.now();

  // Track this animation with type-specific key
  activeAnimations.set(animKey, animation);

  return animation;
};

/**
 * Generates a non-winning reel combination for emergency landing
 * Ensures no winning combinations by using a rotating pattern where:
 * - No symbol appears 3+ times horizontally in adjacent reels
 * - No symbol appears 3+ times vertically/diagonally on any payline
 *
 * Uses symbols 2-9 (h1, m1, m2, l1-l5) excluding coins, wilds, and blanks
 *
 * @returns {number[][]} Array of 5 reels, each with 4 symbol IDs
 */
export const generateNonWinningCombination = () => {
  // Valid symbol pool: IDs 2-9 (h1, m1, m2, l1, l2, l3, l4, l5)
  // Excludes: 0 (blank), 1 (wild), 10+ (coins)
  const validSymbols = [2, 3, 4, 5, 6, 7, 8, 9];

  const grid = [];

  // Generate each reel with a rotating pattern
  // Each reel uses 4 different symbols to ensure no vertical matches
  for (let reel = 0; reel < 5; reel++) {
    const reelSymbols = [];

    // Use different starting offset for each reel to prevent horizontal matches
    const offset = reel * 2; // Shift by 2 for each reel

    for (let row = 0; row < 4; row++) {
      // Pick symbol with offset to ensure variety
      const symbolIndex = (offset + row) % validSymbols.length;
      reelSymbols.push(validSymbols[symbolIndex]);
    }

    grid.push(reelSymbols);
  }

  return grid;
};
